import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_BASE}/auth/forgot-password`, { email });
      setSubmitted(true);
      toast.success('Reset email sent if account exists');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen" data-testid="forgot-password-page">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#FBF8EF]">
        <div className="w-full max-w-md">
          <div className="mb-8 flex flex-col items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_timesheet-ops-1/artifacts/uwk69pjg_Black-8.png" 
              alt="Abule Imole" 
              className="h-32 w-auto mb-2"
            />
            <p className="text-sm text-[#505631] font-medium">Administration Portal</p>
          </div>

          <div className="bg-white rounded-xl border border-[#E5E2D9] p-8 shadow-sm">
            {!submitted ? (
              <>
                <div className="mb-6">
                  <a
                    href="/login"
                    className="inline-flex items-center text-sm text-[#505631] hover:text-[#B2560D] transition-colors"
                    data-testid="back-to-login-link"
                  >
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Back to login
                  </a>
                </div>

                <h2 className="text-xl sm:text-2xl font-medium tracking-tight text-[#2B3210] mb-2">
                  Forgot your password?
                </h2>
                <p className="text-sm text-[#505631] mb-6">
                  Enter your email address and we'll send you a link to reset your password.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#2B3210] mb-1.5">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#A39E8F]" />
                      <input
                        type="email"
                        data-testid="forgot-email-input"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        className="w-full pl-10 pr-4 py-2.5 border border-[#E5E2D9] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B2560D] focus:border-transparent"
                        required
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    data-testid="send-reset-button"
                    disabled={loading}
                    className="w-full bg-[#B2560D] text-white py-2.5 rounded-lg hover:bg-[#8A4209] transition-colors duration-200 font-medium disabled:opacity-50"
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-xl font-medium text-[#2B3210] mb-2">
                  Check your email
                </h2>
                <p className="text-sm text-[#505631] mb-6">
                  If an account exists with <strong>{email}</strong>, you will receive a password reset link shortly.
                </p>
                <p className="text-xs text-[#A39E8F] mb-6">
                  Didn't receive the email? Check your spam folder or try again.
                </p>
                <div className="space-y-3">
                  <button
                    onClick={() => setSubmitted(false)}
                    className="w-full border border-[#B2560D] text-[#B2560D] py-2.5 rounded-lg hover:bg-[#FBF8EF] transition-colors duration-200 font-medium"
                    data-testid="try-again-button"
                  >
                    Try another email
                  </button>
                  <a
                    href="/login"
                    className="block w-full bg-[#B2560D] text-white py-2.5 rounded-lg hover:bg-[#8A4209] transition-colors duration-200 font-medium text-center"
                    data-testid="return-to-login-button"
                  >
                    Return to Login
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Side - Image with color overlay */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1644035656974-9f979761e6a1?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200)',
          }}
        />
        <div 
          className="absolute inset-0"
          style={{
            backgroundColor: '#B2560D',
            mixBlendMode: 'multiply',
            opacity: 0.4,
          }}
        />
      </div>
    </div>
  );
};

export default ForgotPassword;
